import { useEffect, useState, useCallback } from "react";
import { Printer, Plus, Trash2, RefreshCw, CheckCircle2, XCircle, Loader2, Search, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, API } from "@/lib/api";
import { SectorsBox } from "@/components/settings/SectorsBox";
import { qzPrinters, qzPrint } from "@/lib/qz";
import { testPage, LOGO_SIZES } from "@/lib/receipts";
import { isCentral, setCentral } from "@/components/CentralPrinter";
import { PrintPushBox } from "@/components/settings/PrintPushBox";
import { OrderSoundBox } from "@/components/settings/OrderSoundBox";
import { toast } from "sonner";

const DOCS = [["pdv", "Comprovante de venda (PDV e fechamento de comanda/mesa)"], ["ticket", "Tickets da Venda ticket"], ["cash", "Abertura e fechamento de caixa"],
  ["comanda", "Pedido completo de comanda e mesa"], ["delivery", "Pedidos do delivery ao aceitar"]];
const newId = () => Math.random().toString(36).slice(2, 10);
const sel = "h-10 w-full rounded-md border bg-transparent px-2 text-sm";

function NameField({ p, found, onChange }) {
  const [manual, setManual] = useState(false);
  if (manual || !found.length) {
    return (
      <div className="flex min-w-[200px] flex-1 items-center gap-2">
        <Input value={p.name} onChange={(e) => onChange({ name: e.target.value })} placeholder="Nome da impressora no Windows (ex.: POS80)" className="flex-1" data-testid={`printer-name-${p.id}`} />
        {found.length > 0 && <Button size="sm" variant="ghost" onClick={() => setManual(false)} data-testid={`printer-pick-${p.id}`}>Escolher da lista</Button>}
      </div>
    );
  }
  return (
    <select value={found.includes(p.name) ? p.name : ""} onChange={(e) => (e.target.value === "__manual" ? setManual(true) : onChange({ name: e.target.value }))} className={`${sel} min-w-[200px] flex-1`} data-testid={`printer-select-${p.id}`}>
      {!found.includes(p.name) && <option value="">{p.name ? `${p.name} (não encontrada) - escolha outra` : "Escolha a impressora"}</option>}
      {found.map((n) => <option key={n} value={n}>{n}</option>)}
      <option value="__manual">Digitar outro nome...</option>
    </select>
  );
}

function PrinterRow({ p, found, onChange, onRemove, onTest }) {
  const ok = found.includes(p.name);
  return (
    <div className="rounded-xl border p-4 space-y-3" data-testid={`printer-${p.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <Printer className="h-5 w-5 text-muted-foreground" />
        <NameField p={p} found={found} onChange={onChange} />
        <span className={`flex items-center gap-1 text-xs font-semibold ${ok ? "text-emerald-600" : "text-red-500"}`}>{ok ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}{ok ? "Disponível" : "Indisponível"}</span>
        <Button size="sm" variant="outline" onClick={onTest} disabled={!p.name} data-testid={`printer-test-${p.id}`}>Testar</Button>
        <Button size="icon" variant="ghost" onClick={onRemove} data-testid={`printer-remove-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <select value={p.columns} onChange={(e) => onChange({ columns: Number(e.target.value) })} className={sel} data-testid={`printer-columns-${p.id}`}>{[32, 42, 48].map((c) => <option key={c} value={c}>{c} colunas</option>)}</select>
        <label className="flex items-center justify-between rounded-lg border px-3 text-sm">Cortar papel<Switch checked={p.cut} onCheckedChange={(v) => onChange({ cut: v })} data-testid={`printer-cut-${p.id}`} /></label>
        <label className="flex items-center justify-between rounded-lg border px-3 text-sm">Imprimir logo<Switch checked={p.logo} onCheckedChange={(v) => onChange({ logo: v })} data-testid={`printer-logo-${p.id}`} /></label>
        <select value={p.logo_size || "m"} disabled={!p.logo} onChange={(e) => onChange({ logo_size: e.target.value })} className={`${sel} disabled:opacity-50`} data-testid={`printer-logo-size-${p.id}`}>
          {LOGO_SIZES.map(([v, l]) => <option key={v} value={v}>Logo: {l}</option>)}
        </select>
      </div>
    </div>
  );
}

function QzHelp({ onCert }) {
  return (
    <details className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-sm" data-testid="qz-help">
      <summary className="cursor-pointer font-semibold text-amber-600 dark:text-amber-400">Instalou e continua "QZ Tray não encontrado"?</summary>
      <ul className="mt-2 list-disc space-y-1 pl-5 text-muted-foreground">
        <li>Aparece a janela <b>Action Required</b> com escudo vermelho ("Untrusted website")? O certificado não está instalado neste computador: rode o comando do <b>Instalador completo</b> (se o QZ Tray já estiver instalado, ele só grava o certificado). Enquanto o escudo estiver vermelho, "Remember this decision" não funciona.</li>
        <li>Veja se o ícone do QZ Tray aparece perto do relógio do Windows. Se não aparecer, abra <b>QZ Tray</b> no menu Iniciar e clique em Atualizar.</li>
        <li>Rode o comando de novo: pode rodar quantas vezes precisar, ele só completa o que faltou.</li>
        <li>Se aparecer uma janela do QZ Tray pedindo permissão, marque <b>Remember this decision</b> e clique em <b>Allow</b>.</li>
        <li>Se alguém clicou em <b>Block</b>, abra o QZ Tray → Advanced → Site Manager e remova o FSCsistemas da aba Blocked.</li>
        <li>Instalação manual: <a href="https://qz.io/download/" target="_blank" rel="noreferrer" className="underline" data-testid="qz-download-link">baixe o QZ Tray</a> e copie o <button type="button" onClick={onCert} className="underline" data-testid="qz-cert-download-btn">override.crt</button> para C:\Program Files\QZ Tray\.</li>
      </ul>
    </details>
  );
}

const Step = ({ n, children }) => (
  <li className="flex gap-2"><span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-primary/15 text-xs font-bold text-primary">{n}</span><span>{children}</span></li>
);

function QzStatus({ on, onCheck }) {
  return (
    <span className={`flex items-center gap-1 text-sm font-semibold ${on ? "text-emerald-600" : on === false ? "text-red-500" : "text-muted-foreground"}`} data-testid="qz-status">
      {on === null ? <Loader2 className="h-4 w-4 animate-spin" /> : on ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}{on === null ? "Procurando QZ Tray…" : on ? "QZ Tray conectado" : "QZ Tray não encontrado"}
      <Button size="sm" variant="ghost" onClick={onCheck} data-testid="qz-refresh-btn"><RefreshCw className="h-4 w-4 mr-1" />Atualizar</Button>
    </span>
  );
}

const TLS = "[Net.ServicePointManager]::SecurityProtocol='Tls12';";
const SETUP_CMD = `powershell -Command "${TLS}irm ${API}/public/print/setup|iex"`;
const SHORTCUT_CMD = `powershell -Command "${TLS}irm '${API}/public/print/shortcut?origin=${window.location.origin}'|iex"`;

function CommandBox({ cmd, testId, primary }) {
  const copy = () => { navigator.clipboard?.writeText(cmd); toast.success("Comando copiado. Agora aperte Windows + R, cole com Ctrl + V e aperte Enter"); };
  return (
    <div className="space-y-2">
      <code className="block max-h-16 overflow-y-auto break-all rounded-lg border bg-background/60 p-2 font-mono text-[11px] text-muted-foreground" data-testid={`${testId}-text`}>{cmd}</code>
      <Button onClick={copy} variant={primary ? "default" : "outline"} className="w-full" data-testid={testId}><Copy className="h-4 w-4 mr-1" />Copiar comando</Button>
    </div>
  );
}

const RunSteps = ({ last }) => (
  <ol className="space-y-1.5 text-sm">
    <Step n={1}>Clique em <b>Copiar comando</b> abaixo.</Step>
    <Step n={2}>Aperte as teclas <b>Windows + R</b>, cole com <b>Ctrl + V</b> e aperte <b>Enter</b>.</Step>
    <Step n={3}>{last}</Step>
  </ol>
);

function FullOption() {
  return (
    <div className="space-y-3 rounded-xl border-2 border-primary/40 bg-primary/5 p-4" data-testid="print-option-full">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-semibold">Instalador completo</p>
        <span className="rounded-full bg-primary px-2 py-0.5 text-[11px] font-bold text-primary-foreground">Recomendado · várias impressoras</span>
      </div>
      <p className="text-sm text-muted-foreground">Um comando só: instala o QZ Tray, grava o certificado e já deixa o FSCsistemas autorizado, sem janelas para responder. Separa caixa, cozinha e setores e imprime os pedidos do celular.</p>
      <RunSteps last={<>Clique em <b>Sim</b> na permissão do Windows e espere aparecer <b>Pronto!</b>. Depois clique em <b>Atualizar</b> aqui em cima e cadastre as impressoras abaixo.</>} />
      <CommandBox cmd={SETUP_CMD} testId="qz-setup-copy-btn" primary />
    </div>
  );
}

function BrowserOption() {
  return (
    <div className="space-y-3 rounded-xl border p-4" data-testid="print-option-browser">
      <p className="font-semibold">Só uma impressora, sem instalar nada</p>
      <p className="text-sm text-muted-foreground">Cria na área de trabalho o atalho <b>FSC PDV</b> (Chrome ou Edge), que imprime direto na impressora padrão do Windows, sem a janela de imprimir.</p>
      <RunSteps last={<>Abra o sistema sempre pelo atalho <b>FSC PDV</b> (no primeiro uso, entre com o seu login) e, em "Onde cada documento sai", deixe <b>Pela janela do navegador</b>.</>} />
      <p className="text-xs text-amber-600 dark:text-amber-400">Não separa impressoras e não imprime os pedidos do celular. Para isso, use o instalador completo.</p>
      <CommandBox cmd={SHORTCUT_CMD} testId="print-shortcut-copy-btn" />
    </div>
  );
}

function AgentBox({ on, onCheck, onCert }) {
  return (
    <div className="card-surface p-5 space-y-4" data-testid="print-agent-box">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="font-display text-lg font-semibold">Impressão direta neste computador</p>
          <p className="text-sm text-muted-foreground">Comprovantes, tickets e pedidos saem sozinhos, sem a janela de imprimir. Escolha um jeito e faça uma vez em cada computador que imprime. Não precisa baixar arquivo, então o Windows 11 não bloqueia.</p>
        </div>
        <QzStatus on={on} onCheck={onCheck} />
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <FullOption />
        <BrowserOption />
      </div>
      {on === null && <p className="text-xs text-muted-foreground" data-testid="qz-waiting-hint">Se uma janela do QZ Tray estiver aberta, responda nela para continuar.</p>}
      <QzHelp onCert={onCert} />
    </div>
  );
}

function CentralBox() {
  const [on, setOn] = useState(isCentral);
  const toggle = (v) => { setOn(v); setCentral(v); toast.success(v ? "Este computador vai imprimir os pedidos do celular" : "Impressão dos pedidos do celular desligada neste computador"); };
  return (
    <div className="card-surface p-5 space-y-2" data-testid="print-central-box">
      <label className="flex items-center justify-between gap-3">
        <span><span className="font-display text-lg font-semibold">Impressões do app de comandas</span>
          <span className="block text-sm text-muted-foreground">Imprimir pedidos do app de comandas neste computador. Pedidos, contas e comprovantes lançados pelo celular saem direto nas impressoras abaixo, sem janela, enquanto o sistema estiver aberto aqui com o QZ Tray conectado.</span></span>
        <Switch checked={on} onCheckedChange={toggle} data-testid="print-central-switch" />
      </label>
      <p className="text-xs text-muted-foreground">Já vem ligado em todo computador com o QZ Tray conectado. Se a loja tiver mais de um computador com QZ Tray, desligue nos que não têm as impressoras.</p>
      <PrintPushBox central={on} />
    </div>
  );
}

function FoundPrinters({ on, found, configured, onSearch, onAdd }) {
  return (
    <div className="space-y-2 rounded-lg border p-3 text-sm" data-testid="found-printers">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-medium" data-testid="found-printers-summary">
          {on === null ? "Procurando impressoras..." : on ? `${found.length} impressora(s) encontrada(s) neste computador. Clique para adicionar:` : "QZ Tray não conectado. Abra o QZ Tray e clique em Procurar impressoras."}
        </span>
        <Button size="sm" variant="outline" onClick={onSearch} disabled={on === null} data-testid="printers-search-btn">
          {on === null ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Search className="h-4 w-4 mr-1" />}Procurar impressoras
        </Button>
      </div>
      {found.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {found.map((n) => configured.includes(n) ? (
            <span key={n} className="flex items-center gap-1 rounded-full border border-emerald-500/40 px-3 py-1 text-xs text-emerald-600"><CheckCircle2 className="h-3.5 w-3.5" />{n}</span>
          ) : (
            <button key={n} type="button" onClick={() => onAdd(n)} className="flex items-center gap-1 rounded-full border px-3 py-1 text-xs transition-colors hover:bg-muted" data-testid={`found-printer-${n.replace(/\W+/g, "-")}`}><Plus className="h-3.5 w-3.5" />{n}</button>
          ))}
        </div>
      )}
    </div>
  );
}

export function PrintingTab() {
  const { session, refresh } = useAuth();
  const init = session.tenant.settings.printing || {};
  const [cfg, setCfg] = useState({ enabled: !!init.enabled, printers: init.printers || [], routes: init.routes || {}, copies: init.copies || {} });
  const [on, setOn] = useState(null);
  const [found, setFound] = useState([]);
  const check = useCallback(async () => { setOn(null); const list = await qzPrinters().catch(() => null); setFound(list || []); setOn(!!list); }, []);
  useEffect(() => { check(); }, [check]);
  const docs = [...DOCS, ...(session.tenant.settings.production_sectors || []).map((s) => [`sector_${s.id}`, `Setor ${s.name} · pedidos de comanda e mesa`])];
  const setP = (id, patch) => setCfg((c) => ({ ...c, printers: c.printers.map((p) => (p.id === id ? { ...p, ...patch } : p)) }));
  const save = async () => { try { await api.put("/print/config", cfg); await refresh(); toast.success("Configuração de impressão salva"); } catch (e) { toast.error(errMsg(e)); } };
  const test = async (p) => { try { await qzPrint(p.name, testPage(p, session.tenant.nome_fantasia, session.tenant.settings?.logo_url)); toast.success("Teste enviado para a impressora"); } catch (e) { toast.error(e?.message || String(e)); } };
  const addPrinter = (name) => setCfg((c) => ({ ...c, printers: [...c.printers, { id: newId(), name, columns: 42, cut: true, logo: true, logo_size: "m" }] }));
  const download = (path, name) => async () => {
    try {
      const { data } = await api.get(path, { responseType: "text", transformResponse: (d) => d });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([data], { type: "application/octet-stream" }));
      a.download = name;
      a.click();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-6">
      <CentralBox />
      <OrderSoundBox />
      <AgentBox on={on} onCheck={check} onCert={download("/print/certificate", "override.crt")} />
      <div className="card-surface p-5 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="font-display text-lg font-semibold">Impressoras</p>
          <label className="flex items-center gap-2 text-sm font-medium">Impressão direta ativada<Switch checked={cfg.enabled} onCheckedChange={(v) => setCfg((c) => ({ ...c, enabled: v }))} data-testid="print-enabled-switch" /></label>
        </div>
        <FoundPrinters on={on} found={found} configured={cfg.printers.map((p) => p.name)} onSearch={check} onAdd={addPrinter} />
        {cfg.printers.map((p) => <PrinterRow key={p.id} p={p} found={found} onChange={(x) => setP(p.id, x)} onTest={() => test(p)} onRemove={() => setCfg((c) => ({ ...c, printers: c.printers.filter((y) => y.id !== p.id) }))} />)}
        <Button variant="ghost" onClick={() => addPrinter(found.find((n) => !cfg.printers.some((p) => p.name === n)) || "")} data-testid="printer-add-btn"><Plus className="h-4 w-4 mr-1" />Adicionar impressora</Button>
        <p className="text-xs text-muted-foreground">Depois de ajustar, clique em <b>Salvar impressão</b> no fim da página. O botão Testar imprime com o logo no tamanho escolhido.</p>
      </div>
      <SectorsBox />
      <div className="card-surface p-5 space-y-3" data-testid="print-routes">
        <p className="font-display text-lg font-semibold">Onde cada documento sai</p>
        <p className="text-xs text-muted-foreground">Em <b>Vias</b>, escolha quantas cópias saem de cada documento. 0 = não imprime.</p>
        {docs.map(([k, l]) => (
          <div key={k} className="grid items-center gap-2 sm:grid-cols-[1fr_260px_160px]">
            <span className="text-sm">{l}</span>
            <select value={cfg.routes[k] || ""} onChange={(e) => setCfg((c) => ({ ...c, routes: { ...c.routes, [k]: e.target.value } }))} className={sel} data-testid={`print-route-${k}`}>
              <option value="">Pela janela do navegador</option>{cfg.printers.filter((p) => p.name).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <select value={cfg.copies[k] ?? 1} onChange={(e) => setCfg((c) => ({ ...c, copies: { ...c.copies, [k]: Number(e.target.value) } }))} className={sel} data-testid={`print-copies-${k}`}>
              {[0, 1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n === 0 ? "0 · não imprime" : `${n} ${n === 1 ? "via" : "vias"}`}</option>)}
            </select>
          </div>
        ))}
        <Button onClick={save} data-testid="print-save-btn">Salvar impressão</Button>
      </div>
    </div>
  );
}
